import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Stack,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { translateKey } from 'src/locales/translate';
import { mapSpecializationNameIdToString } from 'src/utils/mapSpecializationNameIdToString';
import { GetSpecializationsDto } from 'woofwoof-api';
import * as Yup from 'yup';
import {
  OrganizationServiceFormValues,
  servicePricingFields,
} from './organizationServiceForm';

interface OrganizationServiceFormDialogProps {
  category: string;
  categoryLabel: string;
  visitType: string;
  isEditing: boolean;
  specializations: GetSpecializationsDto[];
  defaultValues: OrganizationServiceFormValues;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (values: OrganizationServiceFormValues) => void;
}

const optionalAmount = (): Yup.NumberSchema<number | undefined> =>
  Yup.number()
    .transform((value, original) => (original === '' ? undefined : value))
    .min(0);

const requiredNumber = (): Yup.NumberSchema<number> =>
  Yup.number()
    .transform((value, original) => (original === '' ? undefined : value))
    .typeError(translateKey('validation.fieldRequired'))
    .required(translateKey('validation.fieldRequired'));

// Mounted only while open, so each opening starts from fresh defaults.
export default function OrganizationServiceFormDialog({
  category,
  categoryLabel,
  visitType,
  isEditing,
  specializations,
  defaultValues,
  isSubmitting,
  onClose,
  onSubmit,
}: OrganizationServiceFormDialogProps): JSX.Element {
  const { showsPrice, showsPrepayment } = servicePricingFields(
    visitType,
    category,
  );

  const schema = Yup.object({
    specializationId: Yup.string().required(
      translateKey('validation.fieldRequired'),
    ),
    name: Yup.string()
      .trim()
      .required(translateKey('validation.fieldRequired')),
    duration: requiredNumber(),
    price: showsPrice ? requiredNumber().min(0) : optionalAmount(),
    // A hidden prepayment field must not block saving with a stale value.
    prepaidAmount: showsPrepayment
      ? optionalAmount().test(
          'not-above-price',
          translateKey('visit.reservationFeeTooHigh'),
          function notAbovePrice(prepaidAmount) {
            const price = Number(this.parent.price);
            return (
              prepaidAmount === undefined ||
              !(price > 0) ||
              prepaidAmount <= price
            );
          },
        )
      : Yup.mixed(),
    description: Yup.string(),
  });

  const methods = useForm<OrganizationServiceFormValues>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  return (
    <Dialog
      open
      onClose={isSubmitting ? undefined : onClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: 'calc(100vw - 32px)', sm: 'calc(100vw - 48px)' },
            m: 0,
          },
        },
      }}
    >
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
        <DialogTitle>
          {isEditing
            ? translateKey('hardcoded.edytuj_usluge')
            : translateKey('hardcoded.dodaj_usluge')}{' '}
          - {categoryLabel}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <RHFTextField
              name="specializationId"
              select
              label={translateKey('search.ui.specialization')}
              required
              helperText={translateKey(
                'hardcoded.wybierz_specjalizacje_skonfigurowana_w_organizacji',
              )}
            >
              {specializations.length === 0 ? (
                <MenuItem disabled>
                  {translateKey(
                    'hardcoded.brak_dostepnych_specjalizacji_dla_tej_kategorii',
                  )}
                </MenuItem>
              ) : (
                specializations.map((specialization) => (
                  <MenuItem key={specialization.id} value={specialization.id}>
                    {mapSpecializationNameIdToString(specialization)}
                  </MenuItem>
                ))
              )}
            </RHFTextField>

            <RHFTextField
              name="name"
              label={translateKey('hardcoded.nazwa_uslugi')}
              required
              placeholder={translateKey(
                'hardcoded.np_badanie_ogolne_szczepienie_konsultacja',
              )}
            />

            <RHFTextField
              name="duration"
              label="Czas trwania (minuty)"
              type="number"
              required
              inputProps={{ min: 10, max: 180, step: 5 }}
              helperText="Czas trwania wizyty w minutach (10-180)"
            />

            {showsPrice && (
              <RHFTextField
                name="price"
                label="Cena"
                type="number"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {translateKey('quickBooking.currencySuffix')}
                    </InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, step: 10 }}
              />
            )}

            {showsPrepayment && (
              <RHFTextField
                name="prepaidAmount"
                label={translateKey('visit.reservationFeeFieldLabel')}
                type="number"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {translateKey('quickBooking.currencySuffix')}
                    </InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, step: 10 }}
                helperText={translateKey('visit.reservationFeeFieldHelper')}
              />
            )}

            <RHFTextField
              name="description"
              label="Opis (opcjonalny)"
              multiline
              rows={3}
              placeholder={translateKey(
                'hardcoded.dodatkowe_informacje_o_usludze',
              )}
            />

            <Alert severity="info">
              {isEditing
                ? translateKey(
                    'hardcoded.zmiany_zostana_zapisane_i_zaktualizowane_w_liscie_uslug',
                  )
                : translateKey(
                    'hardcoded.usluga_zostanie_utworzona_i_automatycznie_dodana_do_listy',
                  )}
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button
            onClick={onClose}
            variant="outlined"
            color="inherit"
            disabled={isSubmitting}
          >
            {translateKey('common.cancel')}
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
          >
            {isEditing
              ? translateKey('hardcoded.zapisz_zmiany')
              : translateKey('hardcoded.dodaj_usluge')}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
